import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDashboardSummary } from '@/hooks/useDashboardSummary';
import { useDashboardKpi } from '@/hooks/useDashboardKpi';
import { useMailHealth } from '@/hooks/useMailHealth';
import { useProjectContext } from '@/project/ProjectContext';
import { kpiRangeForDays, sumMetricsLastDays } from '@/features/dashboard/kpiRollup';
import { DASHBOARD_SECTIONS_42 } from '@/features/dashboard/dashboardSections';
import { useSectionInView } from '@/features/analytics/useSectionInView';
import { DashboardHeroSection } from '@/features/dashboard/DashboardHeroSection';
import { DashboardMailSection } from '@/features/dashboard/DashboardMailSection';
import { DashboardGoogleSection } from '@/features/dashboard/DashboardGoogleSection';
import {
  scrollToSection,
  useScrollSpy,
} from '@/features/analytics/useSectionInView';
import { Button } from '@/components/ui/Button';
import { OnboardingChecklist } from '@/features/onboarding/OnboardingChecklist';
import styles from './DashboardPage.module.css';

export function DashboardPage() {
  const [rangeDays, setRangeDays] = useState<7 | 30 | 90>(30);
  const { selectedProject } = useProjectContext();
  const { from, to } = kpiRangeForDays(rangeDays);

  const summaryQ = useDashboardSummary();
  const kpiQuery = useDashboardKpi(rangeDays);
  const mailHealthQuery = useMailHealth();

  const sectionIds = useMemo(() => DASHBOARD_SECTIONS_42.map((s) => s.id), []);
  const activeSection = useScrollSpy(sectionIds);
  const googleInView = useSectionInView({ rootMargin: '200px 0px' });

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (hash && sectionIds.includes(hash as (typeof sectionIds)[number])) {
      window.requestAnimationFrame(() => scrollToSection(hash));
    }
  }, [sectionIds]);

  const rangeLabel = useMemo(() => {
    const f = new Date(from);
    const t = new Date(to);
    return `${f.toLocaleDateString('nl-NL')} – ${t.toLocaleDateString('nl-NL')}`;
  }, [from, to]);

  const snapshots = kpiQuery.data?.snapshots ?? [];
  const rollup = sumMetricsLastDays(snapshots, rangeDays);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Dashboard</h1>
        <p className={styles.subtitle}>
          {selectedProject ? (
            <>
              <strong>{selectedProject.name}</strong>: operatie, mail en Google. Voor funnel en
              pipeline ga je naar <Link to="/app/analytics">Analytics</Link>.
            </>
          ) : (
            <>
              Alle projecten — operatie &amp; mail. Executive KPI&apos;s in{' '}
              <Link to="/app/analytics">Analytics</Link>.
            </>
          )}
        </p>
      </header>

      <div className={styles.onboardingSlot}>
        <OnboardingChecklist />
      </div>

      <div className={styles.stickyBar}>
        <div className={styles.stickyRow}>
          <div className={styles.periodGroup}>
            {([7, 30, 90] as const).map((d) => (
              <Button
                key={d}
                variant={rangeDays === d ? 'secondary' : 'ghost'}
                onClick={() => setRangeDays(d)}
              >
                {d}d
              </Button>
            ))}
          </div>
          <div className={styles.stickyActions}>
            <span className={styles.rangeLabel}>{rangeLabel}</span>
            <Link to="/app/campagnes/nieuw">
              <Button type="button" variant="secondary">
                Nieuwe campagne
              </Button>
            </Link>
            <Link to="/app/analytics">Analytics</Link>
          </div>
        </div>
        <nav className={styles.sectionNav} aria-label="Dashboard secties">
          {DASHBOARD_SECTIONS_42.map((s) => (
            <a
              key={s.id}
              href={`#${s.hash}`}
              className={`${styles.navLink} ${activeSection === s.id ? styles.navLinkActive : ''}`}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(s.hash);
              }}
            >
              {s.label}
            </a>
          ))}
        </nav>
      </div>

      <section id="overzicht" className={styles.section}>
        <DashboardHeroSection
          rangeDays={rangeDays}
          summary={summaryQ.data}
          summaryLoading={summaryQ.isLoading}
          summaryError={summaryQ.isError ? (summaryQ.error as Error) : null}
          rollup={rollup}
          kpiLoading={kpiQuery.isLoading}
        />
      </section>

      <section id="mail" className={styles.section}>
        <DashboardMailSection
          rangeDays={rangeDays}
          snapshots={snapshots}
          kpiLoading={kpiQuery.isLoading}
          kpiError={kpiQuery.isError ? (kpiQuery.error as Error) : null}
          mailboxes={mailHealthQuery.data?.mailboxes}
          mailLoading={mailHealthQuery.isLoading}
          mailError={mailHealthQuery.isError ? (mailHealthQuery.error as Error) : null}
          mailsSent={rollup.mails_sent}
        />
      </section>

      <section id="google" className={styles.section} ref={googleInView.ref}>
        <DashboardGoogleSection rangeDays={rangeDays} loadData={googleInView.inView} />
      </section>
    </div>
  );
}
